import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { toast } from 'sonner';
import {
  MessageSquare,
  X,
  GripVertical,
  ChevronDown,
  ChevronUp,
  Copy,
  Trash2,
  Plus,
  Save,
  Loader2,
} from 'lucide-react';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChannelPromptsModal = ({ isOpen, onClose, canalId, canalName, onPromptsChanged }) => {
  const { t } = useTranslation();
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Fetch prompts when modal opens
  const fetchPrompts = useCallback(async () => {
    if (!canalId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/prompts/canal/${canalId}`);
      const data = Array.isArray(response.data) ? response.data : [];
      setPrompts(data.map((p, i) => ({ ...p, _order: i, _dirty: false, _isNew: false })));
    } catch (error) {
      console.error('Erro ao buscar prompts:', error);
      toast.error(t('prompts_modal.error_load'));
    } finally {
      setLoading(false);
    }
  }, [canalId, t]);

  useEffect(() => {
    if (isOpen && canalId) {
      fetchPrompts();
      setHasChanges(false);
      setExpandedId(null);
      setDeleteConfirmId(null);
    }
  }, [isOpen, canalId, fetchPrompts]);

  // ESC to close
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Drag & drop reorder
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(prompts);
    const [reordered] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reordered);
    setPrompts(items.map((p, i) => ({ ...p, _order: i })));
    setHasChanges(true);
  };

  // Field changes
  const handleFieldChange = (id, field, value) => {
    setPrompts(prev => prev.map(p =>
      p.id === id ? { ...p, [field]: value, _dirty: true } : p
    ));
    setHasChanges(true);
  };

  // Toggle expand/collapse
  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  // Copy content to clipboard
  const handleCopy = async (conteudo, nome) => {
    try {
      await navigator.clipboard.writeText(conteudo);
      toast.success(`📋 ${t('prompts_modal.copied')}: ${nome}`);
    } catch {
      toast.error(t('prompts_modal.error_copy'));
    }
  };

  // Add new prompt
  const handleAddNew = () => {
    const newPrompt = {
      id: `_new_${Date.now()}`,
      canal_id: canalId,
      nome: '',
      conteudo: '',
      categoria: null,
      anotacoes: null,
      idioma_sugerido: null,
      tags: null,
      favorito: false,
      _order: prompts.length,
      _dirty: true,
      _isNew: true,
    };
    setPrompts([...prompts, newPrompt]);
    setExpandedId(newPrompt.id);
    setHasChanges(true);
  };

  // Delete prompt
  const handleDelete = async (id) => {
    const prompt = prompts.find(p => p.id === id);
    if (!prompt) return;

    // If it's a new unsaved prompt, just remove from local state
    if (prompt._isNew) {
      setPrompts(prev => prev.filter(p => p.id !== id));
      setDeleteConfirmId(null);
      if (expandedId === id) setExpandedId(null);
      return;
    }

    try {
      await axios.delete(`${API}/prompts/${id}`);
      setPrompts(prev => prev.filter(p => p.id !== id));
      setDeleteConfirmId(null);
      if (expandedId === id) setExpandedId(null);
      toast.success(`🗑️ ${t('prompts_modal.deleted')}`);
      if (onPromptsChanged) onPromptsChanged();
    } catch (error) {
      console.error('Erro ao excluir prompt:', error);
      toast.error(t('prompts_modal.error_delete'));
    }
  };

  // Save all changes
  const handleSave = async () => {
    setSaving(true);
    try {
      const promises = [];

      for (const prompt of prompts) {
        if (prompt._isNew && prompt.nome.trim()) {
          // Create new prompt
          promises.push(
            axios.post(`${API}/prompts`, {
              canal_id: canalId,
              nome: prompt.nome.trim(),
              conteudo: prompt.conteudo || '',
              categoria: prompt.categoria || null,
              anotacoes: prompt.anotacoes || null,
              idioma_sugerido: prompt.idioma_sugerido || null,
              tags: prompt.tags || null,
              favorito: prompt.favorito || false,
            })
          );
        } else if (prompt._dirty && !prompt._isNew && prompt.nome.trim()) {
          // Update existing prompt
          promises.push(
            axios.put(`${API}/prompts/${prompt.id}`, {
              canal_id: canalId,
              nome: prompt.nome.trim(),
              conteudo: prompt.conteudo || '',
              categoria: prompt.categoria || null,
              anotacoes: prompt.anotacoes || null,
              idioma_sugerido: prompt.idioma_sugerido || null,
              tags: prompt.tags || null,
              favorito: prompt.favorito || false,
            })
          );
        }
      }

      await Promise.all(promises);
      toast.success(`✅ ${t('prompts_modal.saved')}`);
      setHasChanges(false);
      if (onPromptsChanged) onPromptsChanged();

      // Refresh from server to get real IDs
      await fetchPrompts();
    } catch (error) {
      console.error('Erro ao salvar prompts:', error);
      toast.error(t('prompts_modal.error_save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
        animation: 'dp-modal-overlay 0.2s ease-out',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: 'calc(100% - 32px)', maxWidth: '540px', maxHeight: '85vh',
          margin: '16px',
          background: 'var(--dp-bg-glass, rgba(28,28,28,0.95))',
          backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid var(--dp-border-medium)',
          borderRadius: '16px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
          display: 'flex', flexDirection: 'column',
          animation: 'dp-modal-scale 0.25s ease-out',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid var(--dp-border-subtle)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '10px',
              background: 'rgba(167, 139, 250, 0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <MessageSquare size={18} style={{ color: 'var(--dp-purple-500, #A78BFA)' }} />
            </div>
            <div>
              <h2 style={{
                fontSize: '16px', fontWeight: 700, color: 'var(--dp-text-primary)',
                margin: 0, fontFamily: 'var(--dp-font)',
              }}>
                {t('prompts_modal.title')}
              </h2>
              <p style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)', margin: '2px 0 0' }}>
                {canalName || ''}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', border: '1px solid var(--dp-border-subtle)',
              borderRadius: '8px', padding: '6px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--dp-red-500, #F87171)'; e.currentTarget.style.background = 'rgba(248,113,113,0.08)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.background = 'transparent'; }}
          >
            <X size={16} style={{ color: 'var(--dp-text-tertiary)' }} />
          </button>
        </div>

        {/* Body (scrollable) */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[1, 2, 3].map(i => (
                <div key={i} className="dp-skeleton" style={{ height: '52px', borderRadius: '10px' }} />
              ))}
            </div>
          ) : prompts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 16px' }}>
              <MessageSquare size={40} style={{ color: 'var(--dp-text-tertiary)', margin: '0 auto 12px', opacity: 0.4 }} />
              <p style={{ fontSize: '14px', color: 'var(--dp-text-tertiary)', marginBottom: '4px' }}>
                {t('prompts_modal.empty_title')}
              </p>
              <p style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)', opacity: 0.7 }}>
                {t('prompts_modal.empty_desc')}
              </p>
            </div>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="prompts-list">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {prompts.map((prompt, index) => (
                      <Draggable key={prompt.id} draggableId={prompt.id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            style={{
                              ...provided.draggableProps.style,
                              background: snapshot.isDragging ? 'rgba(167, 139, 250, 0.08)' : 'rgba(255,255,255,0.02)',
                              border: `1px solid ${snapshot.isDragging ? 'rgba(167, 139, 250, 0.30)' : 'var(--dp-border-subtle)'}`,
                              borderRadius: '12px',
                              overflow: 'hidden',
                              transition: snapshot.isDragging ? 'none' : 'all 0.2s ease',
                            }}
                          >
                            {/* Prompt Header Row */}
                            <div style={{
                              display: 'flex', alignItems: 'center', gap: '8px',
                              padding: '12px 14px',
                            }}>
                              {/* Drag Handle */}
                              <div
                                {...provided.dragHandleProps}
                                style={{ cursor: 'grab', flexShrink: 0, display: 'flex', alignItems: 'center' }}
                              >
                                <GripVertical size={16} style={{ color: 'var(--dp-text-tertiary)', opacity: 0.5 }} />
                              </div>

                              {/* Prompt Name (clickable to expand) */}
                              <div
                                onClick={() => toggleExpand(prompt.id)}
                                style={{
                                  flex: 1, minWidth: 0, cursor: 'pointer',
                                  display: 'flex', alignItems: 'center', gap: '8px',
                                }}
                              >
                                <span style={{
                                  fontSize: '13px', fontWeight: 600,
                                  color: prompt.nome ? 'var(--dp-text-primary)' : 'var(--dp-text-tertiary)',
                                  fontFamily: 'var(--dp-font)',
                                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                }}>
                                  {prompt.nome || t('prompts_modal.unnamed')}
                                </span>
                                {expandedId === prompt.id
                                  ? <ChevronUp size={14} style={{ color: 'var(--dp-text-tertiary)', flexShrink: 0 }} />
                                  : <ChevronDown size={14} style={{ color: 'var(--dp-text-tertiary)', flexShrink: 0 }} />
                                }
                              </div>

                              {/* Action Buttons */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                                {/* Copy */}
                                {prompt.conteudo && (
                                  <button
                                    onClick={() => handleCopy(prompt.conteudo, prompt.nome)}
                                    title={t('prompts_modal.copy')}
                                    style={{
                                      background: 'transparent', border: 'none',
                                      borderRadius: '6px', padding: '5px', cursor: 'pointer',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      transition: 'all 0.15s ease',
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(13,174,255,0.10)'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                  >
                                    <Copy size={14} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />
                                  </button>
                                )}

                                {/* Delete */}
                                {deleteConfirmId === prompt.id ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <button
                                      onClick={() => handleDelete(prompt.id)}
                                      style={{
                                        background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.30)',
                                        borderRadius: '6px', padding: '3px 8px', cursor: 'pointer',
                                        fontSize: '10px', fontWeight: 600, color: 'var(--dp-red-500, #F87171)',
                                        fontFamily: 'var(--dp-font)',
                                      }}
                                    >
                                      {t('prompts_modal.confirm_yes')}
                                    </button>
                                    <button
                                      onClick={() => setDeleteConfirmId(null)}
                                      style={{
                                        background: 'transparent', border: '1px solid var(--dp-border-subtle)',
                                        borderRadius: '6px', padding: '3px 8px', cursor: 'pointer',
                                        fontSize: '10px', fontWeight: 500, color: 'var(--dp-text-tertiary)',
                                        fontFamily: 'var(--dp-font)',
                                      }}
                                    >
                                      {t('prompts_modal.confirm_no')}
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => setDeleteConfirmId(prompt.id)}
                                    title={t('prompts_modal.delete')}
                                    style={{
                                      background: 'transparent', border: 'none',
                                      borderRadius: '6px', padding: '5px', cursor: 'pointer',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      transition: 'all 0.15s ease',
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.10)'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                  >
                                    <Trash2 size={14} style={{ color: 'var(--dp-red-500, #F87171)' }} />
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Expanded Content */}
                            {expandedId === prompt.id && (
                              <div style={{
                                padding: '0 14px 14px',
                                borderTop: '1px solid var(--dp-border-subtle)',
                                paddingTop: '14px',
                                animation: 'dp-card-stagger 0.2s ease-out both',
                              }}>
                                {/* Nome field */}
                                <div style={{ marginBottom: '10px' }}>
                                  <label style={{
                                    fontSize: '11px', fontWeight: 600, color: 'var(--dp-text-secondary)',
                                    display: 'block', marginBottom: '4px', fontFamily: 'var(--dp-font)',
                                  }}>
                                    {t('prompts_modal.name_label')}
                                  </label>
                                  <input
                                    type="text"
                                    value={prompt.nome}
                                    onChange={(e) => handleFieldChange(prompt.id, 'nome', e.target.value)}
                                    placeholder={t('prompts_modal.name_placeholder')}
                                    style={{
                                      width: '100%', padding: '8px 12px', borderRadius: '8px',
                                      border: '1px solid var(--dp-border-subtle)',
                                      background: 'var(--dp-bg-input, #2E2E2E)',
                                      color: 'var(--dp-text-primary)', fontSize: '13px',
                                      fontFamily: 'var(--dp-font)', outline: 'none',
                                      transition: 'border-color 0.2s, box-shadow 0.2s',
                                      boxSizing: 'border-box',
                                    }}
                                    onFocus={(e) => { e.target.style.borderColor = 'var(--dp-purple-500, #A78BFA)'; e.target.style.boxShadow = '0 0 0 3px rgba(167,139,250,0.12)'; }}
                                    onBlur={(e) => { e.target.style.borderColor = 'var(--dp-border-subtle)'; e.target.style.boxShadow = 'none'; }}
                                  />
                                </div>

                                {/* Conteudo field */}
                                <div>
                                  <label style={{
                                    fontSize: '11px', fontWeight: 600, color: 'var(--dp-text-secondary)',
                                    display: 'block', marginBottom: '4px', fontFamily: 'var(--dp-font)',
                                  }}>
                                    {t('prompts_modal.content_label')}
                                  </label>
                                  <textarea
                                    value={prompt.conteudo || ''}
                                    onChange={(e) => handleFieldChange(prompt.id, 'conteudo', e.target.value)}
                                    placeholder={t('prompts_modal.content_placeholder')}
                                    rows={4}
                                    style={{
                                      width: '100%', padding: '8px 12px', borderRadius: '8px',
                                      border: '1px solid var(--dp-border-subtle)',
                                      background: 'var(--dp-bg-input, #2E2E2E)',
                                      color: 'var(--dp-text-primary)', fontSize: '13px',
                                      fontFamily: 'var(--dp-font)', outline: 'none',
                                      resize: 'vertical', minHeight: '80px',
                                      transition: 'border-color 0.2s, box-shadow 0.2s',
                                      boxSizing: 'border-box',
                                      lineHeight: 1.5,
                                    }}
                                    onFocus={(e) => { e.target.style.borderColor = 'var(--dp-purple-500, #A78BFA)'; e.target.style.boxShadow = '0 0 0 3px rgba(167,139,250,0.12)'; }}
                                    onBlur={(e) => { e.target.style.borderColor = 'var(--dp-border-subtle)'; e.target.style.boxShadow = 'none'; }}
                                  />
                                  <div style={{ fontSize: '10px', color: 'var(--dp-text-tertiary)', marginTop: '4px', textAlign: 'right' }}>
                                    {(prompt.conteudo || '').length} {t('prompts_modal.chars')}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}

          {/* Add New Button */}
          <button
            onClick={handleAddNew}
            style={{
              width: '100%', padding: '12px', marginTop: '12px',
              borderRadius: '10px', cursor: 'pointer',
              border: '1px dashed var(--dp-border-medium)',
              background: 'transparent',
              color: 'var(--dp-purple-500, #A78BFA)',
              fontSize: '13px', fontWeight: 600, fontFamily: 'var(--dp-font)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(167,139,250,0.06)'; e.currentTarget.style.borderColor = 'var(--dp-purple-500, #A78BFA)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'var(--dp-border-medium)'; }}
          >
            <Plus size={16} />
            {t('prompts_modal.add_new')}
          </button>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid var(--dp-border-subtle)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '12px', flexShrink: 0,
        }}>
          <span style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)' }}>
            {prompts.length} {prompts.length === 1 ? 'prompt' : 'prompts'}
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={onClose}
              style={{
                padding: '8px 20px', borderRadius: '8px', cursor: 'pointer',
                border: '1px solid var(--dp-border-medium)',
                background: 'transparent', color: 'var(--dp-text-secondary)',
                fontSize: '13px', fontWeight: 500, fontFamily: 'var(--dp-font)',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              {t('prompts_modal.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              style={{
                padding: '8px 20px', borderRadius: '8px', cursor: hasChanges && !saving ? 'pointer' : 'default',
                border: 'none',
                background: hasChanges
                  ? 'linear-gradient(135deg, var(--dp-green-500, #34D399), #059669)'
                  : 'rgba(255,255,255,0.06)',
                color: hasChanges ? '#fff' : 'var(--dp-text-tertiary)',
                fontSize: '13px', fontWeight: 600, fontFamily: 'var(--dp-font)',
                display: 'flex', alignItems: 'center', gap: '6px',
                transition: 'all 0.2s ease',
                opacity: hasChanges ? 1 : 0.5,
              }}
            >
              {saving ? (
                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <Save size={14} />
              )}
              {t('prompts_modal.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChannelPromptsModal;
